import { NgTemplateOutlet } from "@angular/common";
import { Component, computed, resource, signal, ViewEncapsulation } from "@angular/core";
import { TypeManagerDecorator } from "../../main/type.map.service";
import { S25EditableTextComponent } from "../../modules/s25-editable/s25-editable-text/s25.editable.text.component";
import { S25IconComponent } from "../../modules/s25-icon/s25.icon.component";
import { S25Icons } from "../../modules/s25-icon/s25.icon.const";
import { Item } from "../../pojo/Item";
import { FavoriteItem, FavoriteService } from "../../services/favorite.service";
import { NameService } from "../../services/name.service";
import { ObjectPermissionService } from "../../services/object.permission.service";
import { OrganizationService } from "../../services/organization.service";
import { PreferenceService } from "../../services/preference.service";
import { ResourceService } from "../../services/resource.service";
import { SpaceService } from "../../services/space.service";
import { UserprefService } from "../../services/userpref.service";
import { S25Const } from "../../util/s25-const";
import { S25Util } from "../../util/s25-util";
import { S25Eval25ScoreComponent } from "../s25-eval25-score/s25.eval25.score.component";
import { S25FavoriteButtonComponent } from "../s25-favorite-button/s25.favorite.button.component";

const EditableItemTypes: Item.Ids[] = [Item.Ids.Organization, Item.Ids.Location, Item.Ids.Resource];
const FormalNameItemTypes: Item.Ids[] = [Item.Ids.Organization, Item.Ids.Location];

@TypeManagerDecorator("s25-ng-item-name")
@Component({
    selector: "s25-ng-item-name",
    template: `
        @if (itemTypeIcon()) {
            <s25-ng-icon
                class="ng-item-name__type-icon"
                [type]="itemTypeIcon()"
                [autoDarkMode]="true"
                svgClass="c-svgIcon {{ isHeaderInput() ? 'page-header' : '' }}"
            ></s25-ng-icon>
        }
        <div class="ng-item-name__core-info">
            <div class="ng-item-name__core-header">
                @if (canEdit() && nameRq.hasValue()) {
                    <s25-ng-editable-text
                        #nameComp
                        class="ng-item-name__name"
                        fieldId="item-name"
                        [class.header]="isHeaderInput()"
                        [val]="name()"
                        [allowEmpty]="false"
                        [readonlyTemplate]="readonlyName"
                        [disabled]="pendingName() !== undefined"
                        [hasCommit]="true"
                        [hasCancelButton]="true"
                        [hasCommitButton]="true"
                        [onCommit]="updateNameFn"
                        [max]="40"
                    ></s25-ng-editable-text>
                } @else {
                    <div class="ng-item-name__name--readonly">
                        <ng-container *ngTemplateOutlet="readonlyName; context: { $implicit: name() }"></ng-container>
                    </div>
                }
                <ng-template #readonlyName let-name>
                    <h1 class="ng-item-name__name-heading">{{ name }}</h1>
                </ng-template>
                @if (favoriteItem()) {
                    <s25-ng-favorite-button
                        class="ng-item-name__favorite-button"
                        [item]="favoriteItem()"
                        (favoriteChange)="updateFavorite($event)"
                    ></s25-ng-favorite-button>
                }
            </div>
            @if (hasFormalName() && !this.formalNameRq.error()) {
                @if (canEdit() && formalNameRq.hasValue()) {
                    <s25-ng-editable-text
                        class="ng-item-name__formal-name"
                        fieldId="item-formal-name"
                        [class.header]="isHeaderInput()"
                        [val]="formalName()"
                        [readonlyTemplate]="readonlyFormalName"
                        [disabled]="pendingFormalName() !== undefined"
                        [hasCommit]="true"
                        [hasCancelButton]="true"
                        [hasCommitButton]="true"
                        [onCommit]="updateFormalNameFn"
                        [weakEditButton]="true"
                        [max]="80"
                    ></s25-ng-editable-text>
                    <ng-template #readonlyFormalName let-formalName>
                        @if (formalName) {
                            {{ formalName }}
                        } @else {
                            <span class="ng-item-name__formal-name-placeholder">+ Add Formal Name</span>
                        }
                    </ng-template>
                } @else if (formalName()) {
                    <div class="ng-item-name__formal-name">
                        {{ formalName() }}
                    </div>
                }
            }
        </div>
        @if (eval25Score()) {
            <s25-ng-eval25-score [score]="eval25Score()"></s25-ng-eval25-score>
        }
    `,
    standalone: true,
    host: {
        "[class.ng-item-name]": "true",
    },
    encapsulation: ViewEncapsulation.Emulated,
    styleUrls: ["./s25.item.name.component.scss"],
    imports: [
        S25Eval25ScoreComponent,
        S25IconComponent,
        S25EditableTextComponent,
        S25FavoriteButtonComponent,
        NgTemplateOutlet,
    ],
})
export class S25ItemNameComponent {
    /** "inputs" */
    public set itemTypeId(value: string | undefined) {
        if (value) {
            let typeId = parseInt(value, 10);
            if (S25Util.isUndefined(Item.Ids[typeId])) {
                throw new Error(`Invalid item type ID: ${value}`);
            }
            this.typeIdInput.set(typeId);
        } else {
            this.typeIdInput.set(undefined);
        }
    }
    public set itemId(value: string | undefined) {
        if (value) {
            this.idInput.set(parseInt(value, 10));
        } else {
            this.idInput.set(undefined);
        }
    }
    public set isHeader(value: boolean) {
        this.isHeaderInput.set(value);
    }

    // TODO: when this is no longer used by angularJS, replace these with transformed `input`s
    //  and delete the fake input setters above
    public readonly typeIdInput = signal<Item.Ids | undefined>(undefined);
    public readonly idInput = signal<number | undefined>(undefined);
    public readonly isHeaderInput = signal<boolean>(false);

    public readonly nameRq = resource({
        params: () => {
            if (this.idInput() && this.typeIdInput()) {
                return { id: this.idInput(), typeId: this.typeIdInput() };
            }
        },
        loader: ({ params }) => NameService.getName(params.id, params.typeId),
    });
    public readonly pendingName = signal<string | undefined>(undefined);
    public readonly name = computed(() => {
        if (this.pendingName() !== undefined) {
            return this.pendingName();
        } else if (this.nameRq.error()) {
            if ((<any>this.nameRq.error()).cause?.data?.results?.error?.msg_id === "RM_I_NfOPERM") {
                return S25Const.private;
            }
        } else if (this.nameRq.isLoading()) {
            return "Loading...";
        } else if (this.nameRq.hasValue()) {
            return this.nameRq.value();
        }
        return "Error Fetching Name";
    });
    public readonly hasFormalName = computed(() => FormalNameItemTypes.includes(this.typeIdInput()));
    public readonly formalNameRq = resource({
        params: () => {
            if (this.hasFormalName() && this.idInput() && this.typeIdInput()) {
                return { id: this.idInput(), typeId: this.typeIdInput() };
            }
        },
        loader: async ({ params }) =>
            (await NameService.getFormal(params.id, params.typeId))
                // handle the fact that organization can return undefined formal name
                ?.trim() ?? "",
    });
    public readonly pendingFormalName = signal<string | undefined>(undefined);
    public readonly formalName = computed(() => {
        if (this.pendingFormalName() !== undefined) {
            return this.pendingFormalName();
        } else if (this.formalNameRq.hasValue()) {
            return this.formalNameRq.value();
        }
    });
    private readonly canEditRq = resource({
        params: () => {
            if (this.idInput() && this.typeIdInput() && EditableItemTypes.includes(this.typeIdInput())) {
                return { id: this.idInput(), typeId: this.typeIdInput() };
            }
        },
        loader: ({ params }) => ObjectPermissionService.canEdit(params.id, params.typeId),
        defaultValue: false,
    });
    public readonly canEdit = computed(() => !!(this.canEditRq.hasValue() && this.canEditRq.value()));
    private readonly eval25EnabledRq = resource({
        params: () => (this.typeIdInput() === Item.Ids.Location ? { prefs: ["Eval25"] } : undefined),
        loader: ({ params }) => PreferenceService.getPreferences(params.prefs),
        defaultValue: { Eval25: { type: "B", value: "false" } },
    });
    private readonly eval25ScoresRq = resource({
        params: () => {
            if (this.eval25EnabledRq.hasValue() && this.eval25EnabledRq.value().Eval25.value === "true") {
                return { id: this.idInput() };
            }
        },
        loader: ({ params }) => SpaceService.getSpaceSatisfaction(params.id),
    });
    public readonly eval25Score = computed(() => {
        if (this.eval25ScoresRq.hasValue()) {
            const scores = this.eval25ScoresRq.value();
            const eval25Data = scores?.length && scores[scores.length - 1]?.rating;

            if (eval25Data) {
                return Math.round(eval25Data * 10) / 10;
            }
        }
    });
    public readonly isLoggedInRq = resource({
        loader: () => UserprefService.getLoggedIn(),
        defaultValue: false,
    });
    public readonly isLoggedIn = computed(() => this.isLoggedInRq.hasValue() && this.isLoggedInRq.value());
    public readonly itemTypeIcon = computed((): S25Icons.Name | undefined => Item.TypeIcons[this.typeIdInput()]);
    public readonly itemTypeName = computed((): string | undefined => Item.Ids[this.typeIdInput() ?? 0]);

    // TODO: move this into the favorite button component?
    public readonly isItemFavoriteRq = resource({
        params: () => {
            if (this.isLoggedIn() && this.idInput() && this.typeIdInput()) {
                return { id: this.idInput(), typeId: this.typeIdInput() };
            }
        },
        loader: ({ params }) => FavoriteService.isFavorite({ itemTypeId: params.typeId, itemId: params.id }),
    });
    public readonly favoriteItem = computed((): FavoriteItem | undefined => {
        if (this.isItemFavoriteRq.hasValue()) {
            return {
                isFav: !!this.isItemFavoriteRq.value().isFavorite?.isFavorite,
                itemId: this.idInput(),
                itemTypeId: this.typeIdInput(),
            };
        }
    });

    public updateFavorite(isFavorite: boolean) {
        this.isItemFavoriteRq.value.set({
            isFavorite: {
                isFavorite: isFavorite ? 1 : 0,
            },
        });
    }

    // lambda to work around [onCommit] being an input instead of output
    public updateNameFn = (newName: string) => this.updateName(newName);
    public updateName(newName: string) {
        if (!this.canEdit() || newName === this.name()) {
            return;
        }
        const updateRq = this.requestUpdateName(newName);
        if (!updateRq) {
            return;
        }
        this.pendingName.set(newName);
        updateRq
            .then(() => this.nameRq.value.set(newName))
            .catch((e) =>
                this.nameRq.value.set(
                    S25Util.errorText(
                        e,
                        "There was an error while updating the object name. Please contact your administrator.",
                    ),
                ),
            )
            .finally(() => this.pendingName.set(undefined));
    }

    // lambda to work around [onCommit] being an input instead of output
    public updateFormalNameFn = (newName: string) => this.updateFormalName(newName);
    public updateFormalName(newName: string = "") {
        if (!this.canEdit() || newName === this.formalName()) {
            return;
        }
        const updateRq = this.requestUpdateFormalName(newName);
        if (!updateRq) {
            return;
        }
        this.pendingFormalName.set(newName);
        updateRq
            .then(() => this.formalNameRq.value.set(newName))
            .catch((e) =>
                this.formalNameRq.value.set(
                    S25Util.errorText(
                        e,
                        "There was an error while updating the object name. Please contact your administrator.",
                    ),
                ),
            )
            .finally(() => this.pendingFormalName.set(undefined));
    }

    private requestUpdateName(newName: string): Promise<void> | undefined {
        switch (this.typeIdInput()) {
            case Item.Ids.Organization:
                return OrganizationService.putOrganization(this.idInput(), {
                    organization_name: newName,
                });
            case Item.Ids.Location:
                return SpaceService.microUpdate({
                    id: this.idInput(),
                    spaceName: newName,
                });
            case Item.Ids.Resource:
                return ResourceService.putResource(this.idInput(), { resource_name: newName });
        }
    }

    private requestUpdateFormalName(newName: string): Promise<void> | undefined {
        switch (this.typeIdInput()) {
            case Item.Ids.Organization:
                return OrganizationService.putOrganization(this.idInput(), {
                    organization_title: newName,
                });
            case Item.Ids.Location:
                return SpaceService.microUpdate({
                    id: this.idInput(),
                    spaceFormalName: newName,
                });
        }
    }
}
